"use client";

import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { usePageSplitting } from "@/hooks/usePageSplitting";
import { useViewerLayout } from "@/hooks/use-viewer-layout";
import { PageModule, type TOCItem } from "@/types/modules";
import { SplitPage } from "@/utils/splitPages";
import { AudienceSessionProvider } from "@/contexts/audience-session-context";
import {
  anchorOf,
  fallbackSplitPages,
  indexForAnchor,
  type PageAnchor,
} from "@/utils/page-anchor";
import { easings, smoothScrollTo } from "@/utils/smooth-scroll";
import {
  buildViewerDetailsIndex,
  type SlideImage,
} from "@/utils/viewer-slide-details";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "./animated-card-diagram";
import { CommentsSection } from "./handyout-components/comments-section";
import HandyoutPage, {
  type EditorInsertTarget,
} from "./handyout-components/handyout-page";
import SlideStack from "./handyout-components/slide-stack";
import { ViewerDetailColumn } from "./handyout-components/viewer-detail-column";
import {
  ViewerChrome,
  ViewerSplitShell,
} from "./handyout-components/viewer-split-shell";
import { OriginalSlideDialog } from "./original-slide-dialog";
import PhoneMockup from "./phone-mockup";
import { BOUNDARY_THROTTLE_MS, SCROLL_DURATION_MS } from "@/config/timing";

export default function Device({
  className,
  pages,
  scrollRef,
  showDeviceMockup = true,
  mode = "editor",
  autoAdvanceMs,
  onModuleClick,
  onScroll,
  onPageChange,
  selectedModuleId,
  selectedModuleLoading,
  onPageClick,
  splitting = true,
  scrollToSlideId,
  windowSize = 3,
  onEmptyClick,
  onInsertClick,
  activeInsertTarget,
  followTheme = false,
  slideImages,
  handyoutTitle,
  toc,
}: {
  className?: string;
  pages: PageModule[];
  scrollRef?: React.RefObject<HTMLDivElement>;
  showDeviceMockup?: boolean;
  mode?: "interactive" | "editor";
  autoAdvanceMs?: number;
  onModuleClick?: (data: any) => void;
  onScroll?: (direction: "up" | "down") => void;
  onPageChange?: (info: {
    currentPage: number;
    totalPages: number;
    moduleIds: string[];
    slideId: string;
    pageIndex: number;
    totalPagesForSlide: number;
  }) => void;

  selectedModuleId?: string | null;
  selectedModuleLoading?: boolean;
  onPageClick?: () => void;
  splitting?: boolean;
  scrollToSlideId?: string;
  windowSize?: number;
  onEmptyClick?: () => void;
  onInsertClick?: (target: EditorInsertTarget) => void;
  activeInsertTarget?: EditorInsertTarget | null;
  followTheme?: boolean;
  slideImages?: Record<string, SlideImage>;
  handyoutTitle?: string;
  toc?: Record<string, TOCItem> | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const programmaticScrollRef = useRef(false);
  const boundaryRef = useRef<{
    firedAt: number;
    direction: "up" | "down" | null;
  }>({ firedAt: 0, direction: null });
  const [currentPage, setCurrentPage] = useState(0);
  const viewerLayout = useViewerLayout(containerRef);
  const pageHeight = viewerLayout.height;
  const moduleColumnWidth = viewerLayout.moduleColumnWidth;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [originalSlideOpen, setOriginalSlideOpen] = useState(false);
  const [positioned, setPositioned] = useState(() => !scrollToSlideId);
  const anchorRef = useRef<PageAnchor | null>(null);
  const anchoredPagesRef = useRef<SplitPage[] | null>(null);

  const slideKey = useMemo(
    () => pages.map((p) => p.slideId).join("|"),
    [pages],
  );

  useIsomorphicLayoutEffect(() => {
    setCurrentPage(0);
    anchorRef.current = null;
    anchoredPagesRef.current = null;
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
    if (scrollToSlideId) setPositioned(false);
  }, [slideKey]);

  const {
    splitPages,
    settled: splitSettled,
    MeasurementContainer,
  } = usePageSplitting({
    pages,
    containerWidth: moduleColumnWidth,
    viewportHeight: pageHeight,
    enabled: splitting && pageHeight > 0 && moduleColumnWidth > 0,
  });

  const displayPages: SplitPage[] = useMemo(
    () => (splitPages.length > 0 ? splitPages : fallbackSplitPages(pages)),
    [splitPages, pages],
  );

  const layoutReady = pageHeight > 0 && (!splitting || splitSettled);

  useIsomorphicLayoutEffect(() => {
    if (positioned || !layoutReady) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const idx = scrollToSlideId
      ? displayPages.findIndex((p) => p.slideId === scrollToSlideId)
      : -1;
    const target = Math.max(idx, 0);
    el.scrollTop = target * pageHeight;
    setCurrentPage(target);
    anchorRef.current = anchorOf(displayPages[target]);
    anchoredPagesRef.current = displayPages;
    setPositioned(true);
  }, [positioned, layoutReady, displayPages, pageHeight, scrollToSlideId]);

  useIsomorphicLayoutEffect(() => {
    if (!positioned) return;
    if (anchoredPagesRef.current !== displayPages) {
      anchoredPagesRef.current = displayPages;
      const anchor = anchorRef.current;
      if (anchor && !programmaticScrollRef.current) {
        const target = indexForAnchor(displayPages, anchor);
        if (target >= 0 && target !== currentPage) {
          const el = scrollContainerRef.current;
          if (el && pageHeight) el.scrollTop = target * pageHeight;
          setCurrentPage(target);
          anchorRef.current = anchorOf(displayPages[target]);
          return;
        }
      }
    }
    anchorRef.current = anchorOf(displayPages[currentPage]) ?? anchorRef.current;
  }, [positioned, displayPages, currentPage, pageHeight]);

  useEffect(() => {
    if (currentPage >= displayPages.length && displayPages.length > 0) {
      const last = displayPages.length - 1;
      setCurrentPage(last);
      const el = scrollContainerRef.current;
      if (el && pageHeight) el.scrollTop = last * pageHeight;
    }
  }, [displayPages.length, currentPage, pageHeight]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !pageHeight) return;
    el.scrollTop = currentPage * pageHeight;
  }, [pageHeight]);

  useEffect(() => {
    if (!positioned) return;
    const page = Math.min(currentPage, displayPages.length - 1);
    const sp = displayPages[page];
    if (!sp) return;
    onPageChange?.({
      currentPage: page,
      totalPages: displayPages.length,
      moduleIds: sp.modules?.map((m: any) => m.id) ?? [],
      slideId: sp.slideId,
      pageIndex: sp.pageIndex,
      totalPagesForSlide: sp.totalPagesForSlide,
    });
  }, [positioned, currentPage, displayPages]);

  const safePageIndex = Math.min(
    Math.max(currentPage, 0),
    Math.max(displayPages.length - 1, 0),
  );
  const currentDisplayPage = displayPages[safePageIndex];
  const currentSlideId = currentDisplayPage?.slideId ?? "";
  const currentSlideImage = slideImages?.[currentSlideId] ?? null;
  const isSplit = viewerLayout.layout === "split";
  const detailsIndex = useMemo(
    () =>
      isSplit
        ? buildViewerDetailsIndex(pages, { title: handyoutTitle }, toc)
        : null,
    [isSplit, pages, handyoutTitle, toc],
  );
  const availableSlideNumbers = useMemo(() => {
    const available = new Set<number>();
    if (!detailsIndex) return available;
    const shown = new Set(displayPages.map((page) => page.slideId));
    detailsIndex.slideIdByNumber.forEach((slideId, slideNumber) => {
      if (shown.has(slideId)) available.add(slideNumber);
    });
    return available;
  }, [detailsIndex, displayPages]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (programmaticScrollRef.current) return;
    const el = e.currentTarget;
    if (!pageHeight) return;
    const nextPage = Math.round(el.scrollTop / pageHeight);
    if (nextPage >= displayPages.length) return;
    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }
  };

  const runProgrammaticScroll = (target: number, onDone?: () => void) => {
    const el = scrollContainerRef.current;
    if (!el || !pageHeight) return;
    programmaticScrollRef.current = true;
    smoothScrollTo(
      el,
      target,
      SCROLL_DURATION_MS,
      easings.easeInOutExpo,
      () => {
        programmaticScrollRef.current = false;
        if (onDone) {
          onDone();
        } else {
          const finalIdx = Math.round(el.scrollTop / pageHeight);
          setCurrentPage(finalIdx);
        }
      },
    );
  };

  useEffect(() => {
    if (!positioned || !scrollToSlideId || !pageHeight) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const idx = displayPages.findIndex((p) => p.slideId === scrollToSlideId);
    if (idx < 0) return;
    const currentIdx = Math.round(el.scrollTop / pageHeight);
    const currentSlideAtCursor = displayPages[currentIdx]?.slideId;
    if (currentSlideAtCursor === scrollToSlideId) return;
    runProgrammaticScroll(idx * pageHeight);
  }, [positioned, scrollToSlideId, pageHeight, displayPages]);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 1;
    const now = Date.now();
    const b = boundaryRef.current;
    if (atTop && e.deltaY < 0) {
      if (now - b.firedAt > BOUNDARY_THROTTLE_MS || b.direction !== "up") {
        b.firedAt = now;
        b.direction = "up";
        onScroll?.("up");
      }
    } else if (atBottom && e.deltaY > 0) {
      if (now - b.firedAt > BOUNDARY_THROTTLE_MS || b.direction !== "down") {
        b.firedAt = now;
        b.direction = "down";
        onScroll?.("down");
      }
    } else {
      b.direction = null;
    }
  };

  useEffect(() => {
    if (!autoAdvanceMs || displayPages.length <= 1) return;
    const id = setInterval(() => {
      const el = scrollContainerRef.current;
      if (!el || !pageHeight) return;
      const cur = Math.round(el.scrollTop / pageHeight);
      const next = cur + 1;
      if (next >= displayPages.length) {
        runProgrammaticScroll(next * pageHeight, () => {
          el.scrollTop = 0;
          setCurrentPage(0);
        });
      } else {
        runProgrammaticScroll(next * pageHeight);
      }
    }, autoAdvanceMs);
    return () => clearInterval(id);
  }, [autoAdvanceMs, displayPages.length, pageHeight]);

  const goToSplitPage = (idx: number) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= displayPages.length) return;
    if (!pageHeight) {
      setCurrentPage(idx);
      return;
    }
    runProgrammaticScroll(idx * pageHeight);
  };

  const jumpToSlideNumber = (slideNumber: number) => {
    const slideId = detailsIndex?.slideIdByNumber.get(slideNumber);
    if (!slideId) return;
    const idx = displayPages.findIndex((page) => page.slideId === slideId);
    if (idx >= 0) goToSplitPage(idx);
  };

  const scrollableContent = (
    <motion.div
      key={slideKey}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="h-full w-full"
    >
      <div
        ref={scrollContainerRef}
        data-device-scroll=""
        onScroll={handleScroll}
        onWheel={handleWheel}
        onClick={onPageClick}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="h-full w-full overflow-y-auto snap-y snap-mandatory overscroll-y-contain [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0"
      >
        <SlideStack
          pages={displayPages}
          pageHeight={pageHeight}
          currentPage={currentPage}
          strategy="windowed"
          windowSize={windowSize}
          renderPage={(page, index) => (
            <HandyoutPage
              sessionId=""
              slideId={page.slideId}
              initModules={page.modules}
              totalPages={displayPages.length}
              currentPage={currentPage + 1}
              pageIndex={index + 1}
              mode={mode}
              className=""
              onModuleClick={onModuleClick}
              onEmptyClick={onEmptyClick}
              onInsertClick={onInsertClick}
              activeInsertTarget={activeInsertTarget}
              selectedModuleId={selectedModuleId}
              selectedModuleLoading={selectedModuleLoading}
            />
          )}
        />
        {autoAdvanceMs &&
          displayPages.length > 1 &&
          displayPages[0] &&
          pageHeight > 0 && (
            <div
              className="snap-start snap-always shrink-0 pointer-events-none"
              style={{ height: pageHeight }}
              aria-hidden
            >
              <HandyoutPage
                sessionId=""
                slideId={`__phantom__${displayPages[0].slideId}`}
                initModules={displayPages[0].modules}
                totalPages={displayPages.length}
                currentPage={1}
                pageIndex={1}
                mode="editor"
                className=""
              />
            </div>
          )}
      </div>
    </motion.div>
  );

  const screen = (
    <ViewerSplitShell
      layout={viewerLayout.layout}
      moduleColumnWidth={moduleColumnWidth}
      detail={
        detailsIndex ? (
          <ViewerDetailColumn
            details={detailsIndex.bySlide.get(currentSlideId) ?? null}
            slideId={currentSlideId}
            slideTotal={detailsIndex.slideTotal}
            image={currentSlideImage}
            titleCard={detailsIndex.titleCard}
            toc={detailsIndex.toc}
            availableSlideNumbers={availableSlideNumbers}
            onJumpToSlide={jumpToSlideNumber}
            onOpenImage={() => setOriginalSlideOpen(true)}
          />
        ) : null
      }
    >
      {scrollableContent}
      <ViewerChrome
        page={currentDisplayPage}
        pageNumber={safePageIndex + 1}
        totalPages={displayPages.length}
      />
      <CommentsSection
        slideId={currentSlideId}
        sessionId=""
        mode="editor"
        isInView={true}
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
      />
    </ViewerSplitShell>
  );

  return (
    <AudienceSessionProvider
      value={{
        sessionId: "",
        presentationId: "",
        existingFeedback: null,
        presenterProfile: null,
        goToSlideNumber: () => {},
        hasSlideNumber: () => false,
        goToSplitPage,
      }}
    >
    <div className="relative h-full w-full flex flex-col items-center justify-center overflow-hidden">
      <MeasurementContainer />
      {showDeviceMockup ? (
        <div className="absolute w-87 h-175">
          <PhoneMockup
            ref={containerRef}
            followTheme={followTheme}
            className={cn("absolute z-40", className)}
            screenClassName="overflow-hidden"
          >
            <div className="relative h-full">{screen}</div>
          </PhoneMockup>
        </div>
      ) : (
        <div
          className="relative w-full h-full flex flex-col"
          ref={containerRef}
        >
          {screen}
        </div>
      )}
    </div>
    <OriginalSlideDialog
      open={originalSlideOpen && !!currentSlideImage}
      onOpenChange={setOriginalSlideOpen}
      url={currentSlideImage?.url ?? null}
      slideNumber={currentSlideImage?.slideNumber ?? null}
    />
    </AudienceSessionProvider>
  );
}
